import React, { useEffect, useState } from "react";
import { Smile, Plus, Trash2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { bustSmilieCache } from "@/components/smilies";

export default function AdminSmilies() {
  const [smilies, setSmilies] = useState([]);
  const [shortcode, setShortcode] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/chat/smilies"); setSmilies(data); } catch {}
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!/^[a-z0-9_]{2,24}$/.test(shortcode)) {
      return toast.error("Shortcode must be 2-24 chars, lowercase letters/numbers/underscore");
    }
    setBusy(true);
    try {
      await api.post("/admin/smilies", { shortcode, url });
      toast.success(`Added :${shortcode}:`);
      setShortcode(""); setUrl("");
      bustSmilieCache();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Add failed");
    } finally { setBusy(false); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Remove :${code}: ?`)) return;
    try { await api.delete(`/admin/smilies/${code}`); toast.success("Removed"); bustSmilieCache(); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <section className="mt-10" data-testid="admin-smilies-section">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Smile className="w-5 h-5 text-[var(--tt-yellow)]" />
          Chat Smilies
        </h2>
        <span className="text-[10px] uppercase tracking-wider bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] px-2 py-0.5 rounded font-semibold">
          {smilies.length}
        </span>
      </div>
      <form onSubmit={add} className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-4 flex flex-wrap gap-3 items-end">
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Shortcode</div>
          <input value={shortcode} onChange={(e) => setShortcode(e.target.value.toLowerCase())} required
            data-testid="smiley-shortcode-input"
            placeholder="e.g. popcorn"
            className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] w-40" />
        </label>
        <label className="block flex-1 min-w-[260px]">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Image URL</div>
          <input value={url} onChange={(e) => setUrl(e.target.value)} required type="url"
            data-testid="smiley-url-input"
            placeholder="https://.../emoji.png"
            className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] w-full font-mono-tt" />
        </label>
        <button type="submit" disabled={busy} data-testid="smiley-add-submit"
          className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-semibold text-sm px-4 py-2 rounded-full flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add
        </button>
      </form>
      <div className="mt-3 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-3 flex flex-wrap gap-2" data-testid="admin-smilies-grid">
        {smilies.map((s) => (
          <div key={s.shortcode} className="flex items-center gap-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full pl-1 pr-2 py-0.5">
            <img src={s.url} alt={s.shortcode} className="w-5 h-5" />
            <span className="text-xs font-mono-tt">:{s.shortcode}:</span>
            <button onClick={() => remove(s.shortcode)} className="text-red-400 hover:text-red-300 ml-1" title="Remove">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {smilies.length === 0 && <div className="text-xs text-[var(--tt-text-muted)] py-2 px-2">No smilies yet.</div>}
      </div>
    </section>
  );
}
